import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Seo from "@/components/feature/Seo";
import { categories, siteMeta } from "@/mocks/site";

const FORM_ENDPOINT = "https://readdy.ai/api/form/dah81u3oh653ivfvnnm0";

const submitSteps = [
  { icon: "ri-inbox-archive-line", title: "接收线索", desc: "提交后进入整理队列，我们优先处理公开可验证的内容。" },
  { icon: "ri-search-eye-line", title: "公开核实", desc: "对照公开信息核对时间、人物与事件脉络，不做主观补充。" },
  { icon: "ri-layout-masonry-line", title: "整理成话题", desc: "把讨论归类、精简，并标注整理时间后发布到对应板块。" },
];

export default function Submit() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("提交成功，感谢你的线索，我们会在核实后处理。");
      setFormError("");
      form.reset();
      return;
    }

    const title = String(formData.get("title") ?? "").trim();
    const category = String(formData.get("category") ?? "").trim();
    const content = String(formData.get("content") ?? "").trim();
    const source = String(formData.get("source") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();

    if (!title || !category || !content || !email) {
      setStatus("error");
      setFormError("请把线索标题、涉及板块、内容概述和联系邮箱填写完整。");
      setFeedback("");
      return;
    }
    if (content.length > 500) {
      setStatus("error");
      setFormError("内容概述请控制在 500 字以内。");
      setFeedback("");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("联系邮箱格式好像不太对，请检查一下。");
      setFeedback("");
      return;
    }

    const params = new URLSearchParams();
    params.append("title", title);
    params.append("category", category);
    params.append("content", content);
    if (source) params.append("source", source);
    params.append("email", email);

    setStatus("loading");
    setFormError("");
    setFeedback("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("提交成功，感谢你的线索，我们会在核实后处理。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && serverMsg.trim().length > 0
            ? serverMsg
            : "提交没有成功，请稍后再试一次。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络好像不太稳定，请稍后再试一次。");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title="投稿爆料 · 把你的线索交给爆料91"
        description="向爆料91投稿爆料线索：填写线索标题、涉及板块与内容概述，我们会核实公开信息后整理成热议话题，让更多网友的讨论被看见。"
        keywords="爆料91,投稿爆料,爆料91热议话题,线索征集"
        path="/submit"
        ogType="website"
      />
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 lg:px-12 pt-6 pb-10 bg-background-100">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "投稿爆料" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
              <i className="ri-send-plane-line"></i>
              投稿爆料
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl font-black text-foreground-950 font-heading leading-tight">
              把你看到的讨论，交给更多人
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              如果你在评论区看到了值得被更多人看到的观点，或者手上有可被公开验证的线索，欢迎提交给我们。请只提交公开范围内、可被验证的内容，不要包含任何隐私信息。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-10">
            <div className="rounded-3xl bg-background-100 border border-background-200 p-5 md:p-8">
              <h2 className="flex items-center gap-2 text-lg md:text-xl font-black text-foreground-950 font-heading">
                <span className="w-7 h-7 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                  <i className="ri-edit-2-line text-sm"></i>
                </span>
                填写线索信息
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                带 <span className="text-primary-600 font-bold">*</span> 为必填项，内容概述不超过 500 字。
              </p>

              <form
                id="baoliao-submit-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div>
                  <label htmlFor="title" className="block text-sm font-bold text-foreground-800">
                    线索标题 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    maxLength={80}
                    required
                    placeholder="用一句话概括这条线索，例如：某综艺赛制调整引发讨论"
                    className="mt-2 w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-bold text-foreground-800">
                    涉及板块 <span className="text-primary-600">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    required
                    defaultValue=""
                    className="mt-2 w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                  >
                    <option value="" disabled>
                      请选择线索所属板块
                    </option>
                    {categories
                      .filter((item) => item.id !== "all")
                      .map((item) => (
                        <option key={item.id} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="content" className="block text-sm font-bold text-foreground-800">
                    内容概述 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    rows={6}
                    maxLength={500}
                    required
                    placeholder="简单说明时间、经过和你观察到的讨论方向，越具体越好。请不要写任何隐私信息。"
                    className="mt-2 w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                  />
                  <p className="mt-1.5 text-xs text-foreground-400">上限 500 字</p>
                </div>

                <div>
                  <label htmlFor="source" className="block text-sm font-bold text-foreground-800">
                    相关来源或链接
                  </label>
                  <input
                    id="source"
                    name="source"
                    type="text"
                    placeholder="公开讨论链接、平台名称等（选填）"
                    className="mt-2 w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-foreground-800">
                    联系邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="用于回复核实结果，我们不会对外公开"
                    className="mt-2 w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="field-assist"
                />

                <label htmlFor="agree" className="flex items-start gap-3 text-sm text-foreground-600">
                  <input
                    id="agree"
                    name="agree"
                    type="checkbox"
                    className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                  />
                  <span>
                    我确认提交的内容来自公开信息，不包含他人隐私，并同意爆料91在核实后进行整理与发布。
                  </span>
                </label>

                {(formError || feedback) && (
                  <div
                    role="status"
                    className={`rounded-xl px-4 py-3 text-sm ${
                      status === "success"
                        ? "bg-secondary-100 text-secondary-900"
                        : "bg-primary-100 text-primary-800"
                    }`}
                  >
                    <i
                      className={
                        status === "success"
                          ? "ri-checkbox-circle-line mr-1.5"
                          : "ri-error-warning-line mr-1.5"
                      }
                    ></i>
                    {formError || feedback}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"}></i>
                    {status === "loading" ? "正在提交…" : "提交线索"}
                  </button>
                  <Link
                    to="/topics"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-background-300 text-foreground-800 text-sm font-bold whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-colors"
                  >
                    先去逛逛热议话题
                  </Link>
                </div>
              </form>
            </div>

            <aside className="flex flex-col gap-5">
              <div className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <span className="w-6 h-6 rounded-md bg-secondary-500 text-background-50 flex items-center justify-center">
                    <i className="ri-route-line text-sm"></i>
                  </span>
                  线索会经历什么
                </h2>
                <ol className="mt-4 flex flex-col gap-4">
                  {submitSteps.map((step, index) => (
                    <li key={step.title} className="flex gap-3">
                      <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
                        <i className={step.icon}></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-bold text-foreground-950">
                          {index + 1}. {step.title}
                        </h3>
                        <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                          {step.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-2xl bg-accent-50 border border-accent-200 p-5 md:p-6">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <span className="w-6 h-6 rounded-md bg-accent-500 text-primary-950 flex items-center justify-center">
                    <i className="ri-error-warning-line text-sm"></i>
                  </span>
                  投稿前请确认
                </h2>
                <ul className="mt-3 flex flex-col gap-2.5 text-sm text-foreground-700">
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5"></i>
                    只提交公开可查的信息
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5"></i>
                    不包含手机号、住址等隐私
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5"></i>
                    不对未经证实的事情下定论
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-700 mt-0.5"></i>
                    留下可联系的邮箱便于回复
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl bg-primary-950 text-background-100 p-5 md:p-6">
                <h2 className="text-base font-bold text-background-50">也可以直接联系</h2>
                <div className="mt-3 flex flex-col gap-2 text-sm text-background-200/80">
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-mail-line text-accent-400"></i>
                    {siteMeta.email}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <i className="ri-time-line text-accent-400"></i>
                    {siteMeta.workingHours}
                  </span>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}